// src/pages/cadastros/Cooperadores/EditarCooperador.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { useTheme } from "../../../contexts/ThemeContext";
import { useAuth } from "../../../contexts/AuthContext";
import { useEmpresa } from "../../../contexts/EmpresaContext";
import { useIsMobile } from "../../../hooks/useIsMobile";

import LayoutDesktop from "../../../layouts/LayoutDesktop";
import LayoutMobile from "../../../layouts/LayoutMobile";

import { pode, nivelUsuario } from "../../../utils/permissoes";

import { db } from "../../../config/firebase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  where,
} from "firebase/firestore";

function labelNivel(u) {
  const n = nivelUsuario(u);
  if (n === 6) return "Gestor";
  if (n === 5) return "Administrador";
  if (n === 4) return "Técnico Sênior";
  if (n === 3) return "Técnico";
  return u?.nivel ?? "-";
}

const COMPETENCIAS_PADRAO = [
  "Elétrica",
  "Eletrônica",
  "Automação",
  "Mecânica",
  "Robótica",
  "Hidráulica",
  "Pneumática",
  "Encanamento",
  "Instrumentação",
  "Metrologia",
  "Refrigeração",
  "Solda",
  "Lubrificação/PCM",
  "Outros",
];

function normalizarCompetencias(list) {
  if (!Array.isArray(list)) return [];
  return list
    .map((x) => String(x || "").trim())
    .filter(Boolean)
    .map((v) => {
      const match = COMPETENCIAS_PADRAO.find((c) => c.toLowerCase() === v.toLowerCase());
      return match || v;
    });
}

export default function EditarCooperador() {
  const { id } = useParams();
  const { tema, alterarTema } = useTheme();
  const { usuario: usuarioLogado } = useAuth();
  const { modoGlobal, empresaAtual } = useEmpresa();
  const navigate = useNavigate();
  const isMobile = useIsMobile(900);

  const canEditar = pode(usuarioLogado, "cadastros", "cooperadores", "altera");

  const [sidebarAberta, setSidebarAberta] = useState(false);

  const [usuarios, setUsuarios] = useState([]);
  const [usuarioBusca, setUsuarioBusca] = useState("");

  const [usuarioId, setUsuarioId] = useState("");
  const [cargo, setCargo] = useState("");

  const [competencias, setCompetencias] = useState([{ tipo: "PADRAO", valor: "" }]);
  const [horarioEntrada, setHorarioEntrada] = useState("");
  const [horarioSaida, setHorarioSaida] = useState("");
  const [ativo, setAtivo] = useState(true);

  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState("");
  const [sucesso, setSucesso] = useState("");

  const inputStyle = {
    width: "100%",
    padding: "9px 11px",
    marginTop: 4,
    borderRadius: 8,
    border: "1px solid #334155",
    boxSizing: "border-box",
  };

  // Carrega usuários elegíveis
  useEffect(() => {
    async function carregarUsuarios() {
      if (!usuarioLogado?.grupoId) return;
      try {
        const ref = collection(db, "usuarios");
        const q = query(ref, where("grupoId", "==", usuarioLogado.grupoId));
        const snap = await getDocs(q);

        const lista = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((u) => {
            const n = nivelUsuario(u);
            return n >= 3; // Técnico+
          })
          .map((u) => ({
            id: u.id,
            nome: u.nome || u.displayName || u.email || u.id,
            nivelLabel: labelNivel(u),
            nivelNum: nivelUsuario(u),
            email: u.email || null,
          }))
          .sort((a, b) => String(a.nome).localeCompare(String(b.nome)));

        setUsuarios(lista);
      } catch (e) {
        console.error("Erro ao carregar usuários:", e);
        setUsuarios([]);
      }
    }

    carregarUsuarios();
  }, [usuarioLogado?.grupoId]);

  // Carrega cooperador
  useEffect(() => {
    async function carregarCooperador() {
      if (!id) return;
      if (!usuarioLogado?.grupoId) return;

      setCarregando(true);
      setErro("");

      try {
        const ref = doc(db, "cooperadores", id);
        const snap = await getDoc(ref);
        if (!snap.exists()) {
          setErro("Cooperador não encontrado.");
          return;
        }
        const d = snap.data() || {};

        // impede edição de outros grupos (defesa no front)
        if (d.grupoId && d.grupoId !== usuarioLogado.grupoId) {
          setErro("Sem acesso a este registro (grupo diferente). ");
          return;
        }

        setUsuarioId(d.usuarioId || "");
        setCargo(d.cargo || "");
        setHorarioEntrada(d.horarioEntrada || "");
        setHorarioSaida(d.horarioSaida || "");
        setAtivo(d.ativo !== false);

        const comps = normalizarCompetencias(d.competencias);
        if (comps.length === 0) {
          setCompetencias([{ tipo: "PADRAO", valor: "" }]);
        } else {
          // transforma para UI (PADRAO/OUTROS)
          const linhas = comps.map((v) => {
            const match = COMPETENCIAS_PADRAO.find((c) => c.toLowerCase() === v.toLowerCase());
            if (match) return { tipo: "PADRAO", valor: match };
            return { tipo: "OUTROS", valor: v };
          });
          setCompetencias(linhas);
        }

      } catch (e) {
        console.error("Erro ao carregar cooperador:", e);
        setErro(e?.message || "Erro ao carregar cooperador.");
      } finally {
        setCarregando(false);
      }
    }

    carregarCooperador();
  }, [id, usuarioLogado?.grupoId]);

  // Atualiza cargo ao escolher usuário
  useEffect(() => {
    if (!usuarioId) return;
    const u = usuarios.find((x) => x.id === usuarioId);
    if (u) setCargo(u.nivelLabel);
  }, [usuarioId, usuarios]);

  const usuariosFiltrados = useMemo(() => {
    const t = usuarioBusca.trim().toLowerCase();
    if (!t) return usuarios;
    return usuarios.filter((u) => String(u.nome).toLowerCase().includes(t));
  }, [usuarios, usuarioBusca]);

  function addCompetencia() {
    setCompetencias((prev) => [...prev, { tipo: "PADRAO", valor: "" }]);
  }

  function removeCompetencia(idx) {
    setCompetencias((prev) => prev.filter((_, i) => i !== idx));
  }

  function setCompetenciaValue(idx, v) {
    setCompetencias((prev) =>
      prev.map((c, i) => {
        if (i !== idx) return c;
        if (v === "Outros") return { tipo: "OUTROS", valor: "" };
        return { tipo: "PADRAO", valor: v };
      })
    );
  }

  function setCompetenciaOutros(idx, v) {
    setCompetencias((prev) => prev.map((c, i) => (i === idx ? { tipo: "OUTROS", valor: v } : c)));
  }

  function buildCompetencias() {
    return competencias
      .map((c) => {
        if (c?.tipo === "OUTROS") return String(c?.valor || "").trim();
        return String(c?.valor || "").trim();
      })
      .filter(Boolean);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setErro("");
    setSucesso("");

    if (!canEditar) {
      setErro("Sem permissão para editar cooperadores.");
      return;
    }

    if (!usuarioLogado?.grupoId) {
      setErro("Usuário sem grupoId. Faça login novamente.");
      return;
    }

    if (!usuarioId) {
      setErro("Selecione um Usuário.");
      return;
    }

    const comps = buildCompetencias();
    if (comps.length === 0) {
      setErro("Informe ao menos 1 competência.");
      return;
    }

    const grupoId = usuarioLogado.grupoId;
    const grupoNome = usuarioLogado.grupoNome || null;

    const empresaId = !modoGlobal && empresaAtual?.id ? empresaAtual.id : null;
    const empresaNome = !modoGlobal && empresaAtual?.nome ? empresaAtual.nome : null;

    const u = usuarios.find((x) => x.id === usuarioId) || null;
    const usuarioNome = u?.nome || usuarioId;
    const usuarioEmail = u?.email || null;
    const cargoFinal = cargo || u?.nivelLabel || "";

    setCarregando(true);

    try {
      const ref = doc(db, "cooperadores", id);
      await setDoc(
        ref,
        {
          usuarioId,
          usuarioNome,
          usuarioEmail,

          cargo: cargoFinal,
          competencias: comps,

          horarioEntrada: (horarioEntrada || "").trim() || null,
          horarioSaida: (horarioSaida || "").trim() || null,

          ativo: !!ativo,

          grupoId,
          grupoNome,
          empresaId,
          empresaNome,

          atualizadoPor: usuarioLogado?.uid || null,
          atualizadoPorNome: usuarioLogado?.nome || usuarioLogado?.displayName || null,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      setSucesso("Cooperador atualizado com sucesso.");
      setTimeout(() => navigate("/cadastros/cooperadores"), 350);
    } catch (e2) {
      console.error("Erro ao atualizar cooperador:", e2);
      setErro(e2?.message || "Erro ao atualizar cooperador.");
    } finally {
      setCarregando(false);
    }
  }

  const Layout = isMobile ? LayoutMobile : LayoutDesktop;

  if (!canEditar) {
    return (
      <Layout
        tema={tema}
        alterarTema={alterarTema}
        titulo="Editar Cooperador"
        sidebarAberta={sidebarAberta}
        onToggleSidebar={() => setSidebarAberta(!sidebarAberta)}
        abaBottom="cadastros"
      >
        <div style={{ padding: 16 }}>
          <h3>Sem permissão</h3>
          <p>Seu usuário não possui permissão para editar cooperadores.</p>
          <button className="pc-btn-secundario" onClick={() => navigate("/cadastros/cooperadores")}>Voltar</button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout
      tema={tema}
      alterarTema={alterarTema}
      titulo="Editar Cooperador"
      sidebarAberta={sidebarAberta}
      onToggleSidebar={() => setSidebarAberta(!sidebarAberta)}
      abaBottom="cadastros"
    >
      <section className="pc-card pc-table-card">
        <header className="pc-table-header">
          <div className="pc-table-header-text">
            <h3>Editar cooperador</h3>
            <p>Registro de colaboradores da manutenção e suas competências.</p>
          </div>
        </header>

        <div style={{ padding: 16 }}>
          {erro && (
            <div
              style={{
                background: "rgba(239, 68, 68, 0.12)",
                border: "1px solid rgba(239, 68, 68, 0.35)",
                padding: 10,
                borderRadius: 10,
                marginBottom: 12,
              }}
            >
              {erro}
            </div>
          )}

          {sucesso && (
            <div
              style={{
                background: "rgba(34, 197, 94, 0.12)",
                border: "1px solid rgba(34, 197, 94, 0.35)",
                padding: 10,
                borderRadius: 10,
                marginBottom: 12,
              }}
            >
              {sucesso}
            </div>
          )}

          <form onSubmit={handleSubmit} style={{ display: "grid", gap: 12, maxWidth: 780 }}>
            {/* Usuário */}
            <div style={{ display: "grid", gap: 8 }}>
              <label>
                <span style={{ fontSize: 14 }}>Buscar usuário</span>
                <input
                  style={inputStyle}
                  value={usuarioBusca}
                  onChange={(e) => setUsuarioBusca(e.target.value)}
                  placeholder="Digite para filtrar a lista..."
                />
              </label>

              <label>
                <span style={{ fontSize: 14 }}>Usuário</span>
                <select
                  style={inputStyle}
                  value={usuarioId}
                  onChange={(e) => setUsuarioId(e.target.value)}
                  required
                >
                  <option value="">Selecione...</option>
                  {usuariosFiltrados.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.nome} — {u.nivelLabel}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {/* Cargo */}
            <label>
              <span style={{ fontSize: 14 }}>Cargo</span>
              <input style={inputStyle} value={cargo} readOnly placeholder="Automático pela escolha do usuário" />
            </label>

            {/* Competências */}
            <div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>Competências</div>
                  <div style={{ fontSize: 12, opacity: 0.75 }}>Adicione quantas competências precisar.</div>
                </div>
                <button type="button" className="pc-btn-secundario" onClick={addCompetencia}>
                  + Adicionar
                </button>
              </div>

              <div style={{ marginTop: 10, display: "grid", gap: 10 }}>
                {competencias.map((c, idx) => {
                  const isOutros = c?.tipo === "OUTROS";
                  const valueSelect = isOutros ? "Outros" : (c?.valor || "");

                  return (
                    <div key={idx} style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 8, alignItems: "start" }}>
                      <div style={{ display: "grid", gap: 8 }}>
                        <select
                          style={inputStyle}
                          value={valueSelect}
                          onChange={(e) => setCompetenciaValue(idx, e.target.value)}
                          required
                        >
                          <option value="">Selecione...</option>
                          {COMPETENCIAS_PADRAO.map((opt) => (
                            <option key={opt} value={opt}>
                              {opt}
                            </option>
                          ))}
                        </select>

                        {isOutros && (
                          <input
                            style={inputStyle}
                            value={c?.valor || ""}
                            onChange={(e) => setCompetenciaOutros(idx, e.target.value)}
                            placeholder="Descreva a competência (Outros)"
                            required
                          />
                        )}
                      </div>

                      <button
                        type="button"
                        className="pc-btn-secundario"
                        onClick={() => removeCompetencia(idx)}
                        disabled={competencias.length <= 1}
                        title={competencias.length <= 1 ? "Mantenha ao menos 1 competência" : "Remover"}
                        style={{ height: 38, marginTop: 4 }}
                      >
                        Remover
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Horários */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <label>
                <span style={{ fontSize: 14 }}>Horário Entrada</span>
                <input
                  type="time"
                  style={inputStyle}
                  value={horarioEntrada}
                  onChange={(e) => setHorarioEntrada(e.target.value)}
                />
              </label>

              <label>
                <span style={{ fontSize: 14 }}>Horário Saída</span>
                <input
                  type="time"
                  style={inputStyle}
                  value={horarioSaida}
                  onChange={(e) => setHorarioSaida(e.target.value)}
                />
              </label>
            </div>

            <label style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 6 }}>
              <input type="checkbox" checked={ativo} onChange={(e) => setAtivo(e.target.checked)} />
              <span style={{ fontSize: 14 }}>Ativo</span>
            </label>

            {/* AÇÕES */}
            <div
              style={{
                marginTop: 12,
                display: "flex",
                justifyContent: "flex-end",
                gap: 8,
              }}
            >
              <button type="button" className="pc-btn-secundario" onClick={() => navigate("/cadastros/cooperadores")}>
                Cancelar
              </button>
              <button type="submit" className="pc-btn-primario" disabled={carregando}>
                {carregando ? "Salvando..." : "Salvar"}
              </button>
            </div>
          </form>
        </div>
      </section>
    </Layout>
  );
}
